import { defaultMemoize } from 'reselect';
import { oc } from 'ts-optchain';
import _ from 'lodash';
import clsx from 'clsx';

import { BasicCarouselWidget } from 'consumer-page-widgets/BasicCarouselWidget';
import {
  BasicCarouselWidgetModifiers,
  BreakpointEnum,
  CallToActionWidgetModifiers,
  CollectionPageWidgetModifiers,
  DividerWidgetModifiers,
  GridWidgetModifiers,
  HeroWidgetModifiers,
  IncludedContentWidgetModifiers,
  MarketingFeaturesWidgetModifiers,
  MarketingWidgetModifiers,
  ModalTextWidgetModifiers,
  StaticHeroWidgetModifiers,
  TitleWidgetModifiers,
  WidgetType,
  Widgets,
} from '@tock/shared/types/widget';
import {
  ConsumerPageAttributes,
  WidgetListItemBusiness,
  WidgetListItemContent,
  WidgetListItemData,
  WidgetListItemExperience,
} from 'types';
import { DisplayDateTime } from '@tock/time';
import { GridWidget } from 'consumer-page-widgets/GridWidget';
import { StoreType } from '@tock/shared/utils/connect';
import { Typography } from 'omakase';
import {
  WidgetCreatorContentProps,
  WidgetSpecificParams,
} from 'components/www/WidgetCreatorContent';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer, core } from '@tock/coretypes';
import { detectUserTimeZoneName } from 'state/app';
import { displayLocation } from '@tock/shared/utils/locationUtils';
import { getAltTextWithFallback, getConcept } from './businessUtils';
import { offeringConsumerDetailPageRelativeLink } from '@tock/shared/utils/offerings';
import Anchor from 'components/Anchor';
import AvatarCardLinkWidget from 'consumer-page-widgets/AvatarCardLinkWidget/AvatarCardLinkWidget';
import CardLinkWidget from 'consumer-page-widgets/CardLinkWidget/CardLinkWidget';
import MarketingFeaturesWidget from 'consumer-page-widgets/MarketingFeaturesWidget';
import MetroAreaContainerWidget from 'consumer-page-widgets/MetroAreaContainerWidget/MetroAreaContainerWidget';
import OfferingsPlatterWidget from 'consumer-page-widgets/OfferingsPlatterWidget/OfferingsPlatterWidget';
import RowLayoutWidget from 'consumer-page-widgets/RowLayoutWidget/RowLayoutWidget';

export const getTockMedium = defaultMemoize((pathname: string) => {
  return (
    (_.first(_.filter(_.split(_.first(_.split(pathname, '?')) || '', '/'))) || 'home') + '_page'
  );
});

export const getTockContent = defaultMemoize((type: string, title?: string) => {
  if (!title) {
    return type;
  }
  return encodeURIComponent(`${type}-${title}`);
});

export const getBodyContent = defaultMemoize((body?: string) => {
  if (!body) {
    return undefined;
  }
  return _.truncate(body, { length: 147, separator: ' ' });
});

export const mapToWidgetData = defaultMemoize(
  (widgetContent: consumer.WidgetContent): WidgetListItemData[] => {
    const type = widgetContent.contentType;
    const timeZone = detectUserTimeZoneName();
    switch (type) {
      case 'BUSINESS': {
        const businesses = widgetContent.business as consumer.OfferingBusiness[];
        return _.map(businesses, (business) => {
          const highlightedNote = business.nextReleaseActiveOnEpochMilli ? (
            <span>
              <DisplayDateTime
                dateTimeValue={new Date(business.nextReleaseActiveOnEpochMilli)}
                showYear={false}
                showWeekday={true}
              />{' '}
              {timeZone}
            </span>
          ) : (
            ''
          );

          const image: core.IUploadedImage | undefined = oc(business).profileImage[0]();
          return {
            relativeLink: `/${business.domainName}`,
            imgSrc: oc(image).imageUrl('') || business.heroImageUrl,

            dominantColor: image?.dominantColor ?? '',
            altText: getAltTextWithFallback(oc(image).altText(), business),
            title: business.name,
            subtitleString: displayLocation(business),
            subtitle: getSubtitleForBusiness(business),
            body: business.description,
            highlightedNote,
            key: _.toString(business.id),
            type,
          };
        });
      }
      case 'EXPERIENCE': {
        const offerings = widgetContent.offering as consumer.OfferingAvailability[];
        return _.map(offerings, (offering) => {
          const experience = offering.offering as consumer.Offering;
          const business = offering.business as consumer.OfferingBusiness;
          const image = _.first(experience.image) || _.first(business.profileImage);
          return {
            relativeLink: offeringConsumerDetailPageRelativeLink(experience, business.domainName),
            imgSrc: oc(image).imageUrl(''),

            dominantColor: image?.dominantColor ?? '',
            altText: getAltTextWithFallback(oc(image).altText(), business, experience),
            title: experience.name,
            subtitleString: getExperienceLocation(business, experience),
            subtitle: getSubtitleForExperience(business),
            body: experience.description,
            key: _.toString(experience.id),
            type,
          };
        });
      }
      case 'METRO_AREA': {
        const metroAreas = widgetContent.metroArea as consumer.MetroArea[];
        return _.map(metroAreas, (metroArea) => {
          return {
            relativeLink: `/${metroArea.slug}`,
            title: metroArea.name,
            subtitleString: metroArea.description,
            body: metroArea.description,
            key: metroArea.slug,
            altText: metroArea.name,
            dominantColor: '#e9e9eb',
            type,
          };
        });
      }
      default: {
        return [];
      }
    }
  }
);

type ImageUrlProvider = 'IMGIX' | 'LEGACY';

export const mapBusinessDataToItems = defaultMemoize(
  (
    businessData: consumer.IOfferingBusiness[],
    imageUrlType: ImageUrlProvider = 'LEGACY'
  ): WidgetListItemBusiness[] => {
    const timeZone = detectUserTimeZoneName();
    const businesses = businessData;
    if (businesses.length === 0) {
      return [];
    }
    return businesses.map((business) => {
      const highlightedNote = business.nextReleaseActiveOnEpochMilli ? (
        <span>
          <DisplayDateTime
            dateTimeValue={new Date(business.nextReleaseActiveOnEpochMilli)}
            showYear={false}
            showWeekday={true}
          />{' '}
          {timeZone}
        </span>
      ) : (
        ''
      );

      const image: core.IUploadedImage | undefined = oc(business).profileImage[0]();
      const imageBackingUrl = image?.imageBackingUrl ?? '';
      const imageUrl = image?.imageUrl ?? '';
      const businessItem: WidgetListItemBusiness = {
        relativeLink: `/${business.domainName}`,
        imgSrc:
          (imageUrlType === 'IMGIX' ? imageBackingUrl : imageUrl || business.heroImageUrl) ||
          undefined,
        dominantColor: image?.dominantColor || '',
        altText: getAltTextWithFallback(image?.altText, business),
        title: business.name || '',
        subtitleString: displayLocation(business),
        subtitle: getSubtitleBusiness(business),
        subtitleDetailed: getSubtitleBusiness(business, 'long'),
        body: business.description || undefined,
        highlightedNote,
        key: _.toString(business.id),
        type: 'BUSINESS',
        business,
      };

      return businessItem;
    });
  }
);

export const mapExperienceDataToItems = defaultMemoize(
  (experienceData: consumer.IOfferingAvailability[]): WidgetListItemExperience[] => {
    return experienceData.map((offering) => {
      const experience = offering.offering as consumer.Offering;
      const business = offering.business as consumer.OfferingBusiness;
      const image = _.first(experience.image) || _.first(business.profileImage);
      const offeringPrice = offering.offering?.price;
      return {
        relativeLink: offeringConsumerDetailPageRelativeLink(experience, business.domainName),
        date: experience.eventDetails?.date ?? undefined, // to handle the I version of this proto
        imgSrc: image?.imageUrl || '',
        dominantColor: image?.dominantColor ?? '',
        altText: getAltTextWithFallback(image?.altText, business, experience),
        title: experience.name,
        subtitleString: getExperienceLocation(business, experience),
        location: getExperienceLocation(business, experience),
        subtitle: business.name,
        body: experience.description,
        key: _.toString(experience.id),
        price: offeringPrice || undefined,
        schedule: (experience.eventDetails?.schedule?.[0] as core.Schedule) ?? undefined,
        business,
        experience,
        type: 'EXPERIENCE',
      };
    });
  }
);

// used in new widget cards
export const mapContentToItems = defaultMemoize(
  (
    widgetContent?: consumer.WidgetContent,
    imageUrlType: ImageUrlProvider = 'LEGACY'
  ): WidgetListItemContent[] => {
    if (!widgetContent) {
      return [];
    }
    const type = widgetContent.contentType;
    const timeZone = detectUserTimeZoneName();
    switch (type) {
      case 'BUSINESS': {
        const businesses = widgetContent.business as consumer.OfferingBusiness[];
        if (businesses.length === 0) {
          return [];
        }
        return businesses.map((business) => {
          const highlightedNote = business.nextReleaseActiveOnEpochMilli ? (
            <span>
              <DisplayDateTime
                dateTimeValue={new Date(business.nextReleaseActiveOnEpochMilli)}
                showYear={false}
                showWeekday={true}
              />{' '}
              {timeZone}
            </span>
          ) : (
            ''
          );

          const image: core.IUploadedImage | undefined = oc(business).profileImage[0]();
          const imageBackingUrl = image?.imageBackingUrl ?? '';
          const imageUrl = image?.imageUrl ?? '';
          const businessItem: WidgetListItemBusiness = {
            relativeLink: `/${business.domainName}`,
            imgSrc: imageUrlType === 'IMGIX' ? imageBackingUrl : imageUrl || business.heroImageUrl,

            dominantColor: image?.dominantColor ?? '',
            altText: getAltTextWithFallback(oc(image).altText(), business),
            title: business.name,
            subtitleString: displayLocation(business),
            subtitle: getSubtitleBusiness(business),
            subtitleDetailed: getSubtitleBusiness(business, 'long'),
            body: business.description,
            highlightedNote,
            key: _.toString(business.id),
            type,
            business,
          };
          return businessItem;
        });
      }
      case 'BUSINESS_GROUP': {
        const businessGroups = widgetContent.businessGroup as consumer.OfferingBusinessGroup[];
        if (businessGroups.length === 0) {
          return [];
        }
        return businessGroups.map((businessGroup) => {
          const image: core.IUploadedImage | undefined = businessGroup.image ?? undefined;
          return {
            relativeLink: `/group/${businessGroup.slug}`,
            imgSrc: image?.imageUrl ?? undefined,
            dominantColor: image?.dominantColor ?? '',
            altText: `${businessGroup.name} image`,
            title: businessGroup.name,
            key: _.toString(businessGroup.id),
            type,
          };
        });
      }
      case 'EXPERIENCE': {
        const offerings = widgetContent.offering as consumer.OfferingAvailability[];
        return mapExperienceDataToItems(offerings);
      }
      case 'METRO_AREA': {
        const metroAreas = widgetContent.metroArea as consumer.MetroArea[];
        return _.map(metroAreas, (metroArea) => {
          return {
            relativeLink: `/${metroArea.slug}`,
            title: metroArea.name,
            subtitleString: metroArea.description,
            body: metroArea.description,
            key: metroArea.slug,
            altText: metroArea.name,
            dominantColor: '#e9e9eb',
            type,
          };
        });
      }
      default: {
        return [];
      }
    }
  }
);

function getSubtitleFromBusiness(business: consumer.OfferingBusiness) {
  return {
    lineOne: `
      ${displayLocation(business)}${business.neighborhood ? ` - ${business.neighborhood}` : ''}`,
    lineTwo: getConcept(business),
  };
}

function buildSubtitleFromBusiness(
  business: consumer.IOfferingBusiness,
  displayType: 'short' | 'long' = 'short'
) {
  if (displayType === 'long') {
    return {
      lineOne: `
        ${displayLocation(business)}${business.neighborhood ? ` - ${business.neighborhood}` : ''}`,
      lineTwo: getConcept(business),
    };
  }
  return {
    lineOne: `
        ${displayLocation(business)}`,
    lineTwo: getConcept(business),
  };
}

function getSubtitleForExperience(business: consumer.OfferingBusiness): JSX.Element {
  const cityText = displayLocation(business);
  return (
    <div>
      <Typography color="inherit" component="span" variant="body02">
        <Anchor href={`/${business.domainName}`}>{business.name}</Anchor> - {cityText}
      </Typography>
    </div>
  );
}

// used in new widgets
function getExperienceLocation(
  business: consumer.OfferingBusiness,
  offering: consumer.Offering
): string {
  return displayLocation(offering.eventDetails?.location ?? business);
}

function getSubtitleForBusiness(business: consumer.OfferingBusiness): JSX.Element {
  const subtitle = getSubtitleFromBusiness(business);
  return (
    <Typography color="inherit" component="span" variant="body02">
      {subtitle.lineOne} &#183; {subtitle.lineTwo}
    </Typography>
  );
}

// used in new widgets
function getSubtitleBusiness(
  business: consumer.IOfferingBusiness,
  displayType: 'short' | 'long' = 'short'
): JSX.Element {
  const subtitle = buildSubtitleFromBusiness(business, displayType);
  return (
    <>
      {subtitle.lineOne} <br /> {subtitle.lineTwo}
    </>
  );
}

export function getDataLength(content: consumer.WidgetContent): number {
  const type = content.contentType;
  let length = 0;
  switch (type) {
    case 'BUSINESS': {
      length = content.business.length;
      break;
    }
    case 'EXPERIENCE': {
      length = content.offering.length;
      break;
    }
    case 'METRO_AREA': {
      length = content.metroArea.length;
      break;
    }
    case 'BUSINESS_GROUP': {
      length = content.businessGroup.length;
      break;
    }
    case 'NAMED_CONFIGURATION': {
      length = 0;
      break;
    }
    case 'SHOP_ITEM': {
      length = content.shopItemAvailability.length;
      break;
    }
    case 'SHOP_BRAND': {
      length = content.shopBrandAvailability.length;
      break;
    }
    case 'SHOP_WINE_REGION': {
      length = content.shopWineRegion.length;
      break;
    }
    default: {
      assertNever(type);
    }
  }
  return length;
}

const DEFAULT_ATTRIBUTES: ConsumerPageAttributes = {
  title: 'Tock - Discover unique culinary experiences',
  description:
    'Book reservations and culinary experiences at restaurants, bars, wineries and pop-ups across the globe.',
  keywords:
    'Prix fixe, tickets, bookings, reservation, restaurant, tasting menu, omakase, fine dining, dining, tock, book your experience',
};

export function getDocumentAttributesFromTemplate(
  consumerPage: StoreType['consumerPage']['consumerPage'],
  pathname: string
): ConsumerPageAttributes {
  const consumerPageSelected = consumerPage[pathname];
  if (!consumerPageSelected) {
    return DEFAULT_ATTRIBUTES;
  }
  const consumerPageVariant = consumerPageSelected.variant[0];
  const consumerPageTemplate = oc(consumerPageVariant).template() as consumer.ConsumerPageTemplate;
  return {
    title: consumerPageTemplate.title || DEFAULT_ATTRIBUTES.title,
    description: consumerPageTemplate.description || DEFAULT_ATTRIBUTES.description,
    keywords: consumerPageTemplate.keywords + ', ' + DEFAULT_ATTRIBUTES.keywords,
    jsonMicrodata: consumerPageTemplate.jsonMicrodata,
    image: consumerPageTemplate.image,
  };
}

type WidgetModifier = { [k: string]: consumer.IWidgetModifier };

export const getWidgetTypes = defaultMemoize((widgets: consumer.Widget[]): WidgetType[] => {
  return _.map(widgets, (widget) => {
    const widgetInfo = widget.widgetInfo as consumer.WidgetInfo;

    const {
      altText,
      bodyText,
      contentColumnA,
      contentColumnB,
      decorativeHeroImageUrl,
      decorativeHeroImageUrlDesktop,
      decorativeHeroImageAltText,
      decorativeColorHex,
      density,
      imageUrl,
      includedContentUrl,
      isAlignedCenter,
      layout,
      linkText,
      marketingFeaturesSetting,
      modalBody,
      modalTitle,
      ribbonText,
      showDescription,
      useWiderSquares,
    } = widgetInfo.widgetModifier.reduce<WidgetModifier>((obj, modifier) => {
      if (modifier.key) {
        obj[modifier.key] = modifier;
      }
      return obj;
    }, {});

    const renderType = widgetInfo.renderType as Widgets;

    switch (renderType) {
      case Widgets.Title: {
        return {
          ...widget,
          renderType,
          widgetModifierFields: [isAlignedCenter] as TitleWidgetModifiers,
        };
      }
      case Widgets.Grid: {
        return {
          ...widget,
          renderType,
          widgetModifierFields: [
            showDescription,
            isAlignedCenter,
            useWiderSquares,
            density,
          ] as GridWidgetModifiers,
        };
      }
      case Widgets.BasicCarousel: {
        return {
          ...widget,
          renderType,
          widgetModifierFields: [isAlignedCenter] as BasicCarouselWidgetModifiers,
        };
      }
      case Widgets.Hero: {
        return {
          ...widget,
          renderType,
          widgetModifierFields: [isAlignedCenter] as HeroWidgetModifiers,
        };
      }
      case Widgets.StaticHero: {
        return {
          ...widget,
          renderType,
          widgetModifierFields: [imageUrl] as StaticHeroWidgetModifiers,
        };
      }
      case Widgets.Divider: {
        return {
          ...widget,
          renderType,
          widgetModifierFields: [] as DividerWidgetModifiers,
        };
      }
      case Widgets.CallToAction: {
        return {
          ...widget,
          renderType,
          widgetModifierFields: [
            imageUrl,
            bodyText,
            altText,
            layout,
          ] as CallToActionWidgetModifiers,
        };
      }
      case Widgets.ModalText: {
        return {
          ...widget,
          renderType,
          widgetModifierFields: [
            ribbonText,
            linkText,
            modalTitle,
            modalBody,
          ] as ModalTextWidgetModifiers,
        };
      }
      case Widgets.Marketing: {
        return {
          ...widget,
          renderType,
          widgetModifierFields: [contentColumnA, contentColumnB] as MarketingWidgetModifiers,
        };
      }
      case Widgets.MarketingFeatures: {
        return {
          ...widget,
          renderType,
          widgetModifierFields: [marketingFeaturesSetting] as MarketingFeaturesWidgetModifiers,
        };
      }
      case Widgets.CollectionPage: {
        return {
          ...widget,
          renderType,
          widgetModifierFields: [
            decorativeHeroImageUrl,
            decorativeHeroImageUrlDesktop,
            decorativeHeroImageAltText,
            decorativeColorHex,
          ] as CollectionPageWidgetModifiers,
        };
      }
      case Widgets.IncludedContent: {
        return {
          ...widget,
          renderType,
          widgetModifierFields: [includedContentUrl].filter(
            (x) => x
          ) as IncludedContentWidgetModifiers,
        };
      }
      case Widgets.WidgetCreatorWidgetType: {
        return {
          ...widget,
          renderType,
        };
      }
      default: {
        return assertNever(renderType);
      }
    }
  });
});

export function getModifierClassName(
  originalClass: string,
  modifier: consumer.WidgetModifier.Breakpoint[]
) {
  if (_.includes(modifier, BreakpointEnum.TabletLandscape)) {
    return clsx(originalClass, {
      'is-tablet': true,
    });
  } else if (_.includes(modifier, BreakpointEnum.DesktopRegular)) {
    return clsx(originalClass, {
      'is-desktop': true,
    });
  }
  return clsx(originalClass);
}

export function getDisplayComponentForWidget(
  widget: consumer.Widget,
  { onChangeIsShopHeroInView }: WidgetSpecificParams,
  pageWidgetListIndex: number,
  options: WidgetCreatorContentProps['options']
) {
  const widgetTypeCase = widget.widget;

  if (!widgetTypeCase) {
    return null;
  }

  const widgetInfo = widget.widgetInfo ?? undefined;

  switch (widgetTypeCase) {
    case 'cardLinkWidget': {
      const cardLinkWidget = widget.cardLinkWidget;
      return (
        cardLinkWidget && (
          <CardLinkWidget
            widget={cardLinkWidget}
            widgetInfo={widgetInfo}
            pageWidgetListIndex={pageWidgetListIndex}
          />
        )
      );
    }
    case 'rowLayoutWidget': {
      const rowLayoutWidget = widget.rowLayoutWidget;
      return (
        rowLayoutWidget && (
          <RowLayoutWidget
            widget={rowLayoutWidget}
            widgetInfo={widgetInfo}
            pageWidgetListIndex={pageWidgetListIndex}
          />
        )
      );
    }
    case 'avatarCardLinkWidget': {
      const avatarCardLinkWidget = widget.avatarCardLinkWidget;
      return (
        avatarCardLinkWidget && (
          <AvatarCardLinkWidget
            widget={avatarCardLinkWidget}
            widgetInfo={widgetInfo}
            pageWidgetListIndex={pageWidgetListIndex}
          />
        )
      );
    }
    case 'gridWidget': {
      const gridWidget = widget.gridWidget;
      const displayType = gridWidget?.displayType;

      switch (displayType) {
        case 'GRID':
          return (
            gridWidget && (
              <GridWidget
                widget={gridWidget}
                key={pageWidgetListIndex}
                lazyLoad={options.lazyLoad}
                fetchMoreWidgetData={options.fetchMoreWidgetData}
                pageType={options.type}
                slug={options.pathname}
                pathname={options.pathname}
                className={options.className}
                isTitle={options.isTitle}
                widgetListIndex={pageWidgetListIndex}
              />
            )
          );
        case 'CAROUSEL':
          return (
            gridWidget && (
              <BasicCarouselWidget
                isTitle={options.isTitle}
                lazyLoad={options.lazyLoad || true}
                pathname={options.pathname}
                widget={{ mode: 'widgetBuilder', carousel: gridWidget }}
              />
            )
          );

        default:
          return <></>;
      }
    }
    case 'marketingFeaturesWidget': {
      const marketingFeaturesWidget = widget.marketingFeaturesWidget;

      return (
        marketingFeaturesWidget && (
          <MarketingFeaturesWidget
            name={marketingFeaturesWidget.name || undefined}
            tile={marketingFeaturesWidget.tile || []}
            pageIndex={pageWidgetListIndex}
          />
        )
      );
    }
    case 'metroAreaContainerWidget': {
      const metroAreaContainerWidget = widget.metroAreaContainerWidget;
      return (
        metroAreaContainerWidget && (
          <MetroAreaContainerWidget
            widget={metroAreaContainerWidget}
            widgetInfo={widgetInfo}
            pageWidgetListIndex={pageWidgetListIndex}
            options={options}
          />
        )
      );
    }
    case 'offeringsPlatterWidget': {
      const offeringsPlatterWidget = widget.offeringsPlatterWidget;
      return (
        offeringsPlatterWidget && (
          <OfferingsPlatterWidget
            widget={offeringsPlatterWidget}
            widgetInfo={widgetInfo}
            pageWidgetListIndex={pageWidgetListIndex}
            options={options}
          />
        )
      );
    }
    case 'buttonArrayWidget':
    case 'shopHeroWidget':
      return <></>;
    default: {
      assertNever(widgetTypeCase);
    }
  }
}
